import React, { useState, useEffect, useContext, useCallback } from "react";
import "./Style.css";
import MenuBar from "./MenuBar";
import { Button } from "@mui/material";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";
import { NotificationContext } from "../redux/NotificationProvider";
import DeleteModal from "./DeleteModal";
const envVariable = require("../EnvironmentVariables.json");

const DeletedTasks = () => {
  const itemsPerPage = 6;
  const [currentPage, setCurrentPage] = useState(1);
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [taskToRestore, setTaskToRestore] = useState(null);
  const navigate = useNavigate();
  const [searchByCustomer, setSearchByCustomer] = useState("");
  const [searchByVIN, setSearchByVIN] = useState("");
  const notify = useContext(NotificationContext);
  const [sortField, setSortField] = useState("deleted_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // Parse datetime string from backend as UTC (backend stores in UTC)
  const parseUTCDateTime = (dateString) => {
    if (!dateString) return null;
    // Backend returns format: "YYYY-MM-DD HH:MM:SS"
    // Replace space with T and add Z to indicate UTC
    const isoString = dateString.replace(' ', 'T') + (dateString.includes('Z') ? '' : 'Z');
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? null : date;
  };

  // Format datetime for display with space instead of T, converted to local timezone
  const formatLocalDateTimeDisplay = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : parseUTCDateTime(date);
    if (!d || isNaN(d.getTime())) return '';

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Helper function to safely format dates
  const formatDateSafe = (dateValue) => {
    if (!dateValue) {
      // If no date provided, use today's date as fallback
      return new Date().toISOString().split("T")[0];
    }

    const date = new Date(dateValue);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('[DeletedTasks] Invalid date value:', dateValue, '- using today as fallback');
      return new Date().toISOString().split("T")[0];
    }

    return date.toISOString().split("T")[0];
  };

  const fetchDeletedTasks = async () => {
    const SC_Id = localStorage.getItem("SC_Id");

    const url = new URL(envVariable.serverId + "/SCO/getDeletedTasks.php");
    url.searchParams.append("sc_id", SC_Id);

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.type === "error") {
        console.error(data.message);
        setTasks([]);
        setFilteredTasks([]);
      } else if (data.type === "success" && Array.isArray(data.results)) {
        setTasks(data.results);
        setCurrentPage(1);
      } else {
        setTasks([]);
        setFilteredTasks([]);
        throw new Error("Unexpected response structure");
      }
    } catch (error) {
      console.error("Failed to fetch deleted tasks:", error);
      notify("Failed to fetch deleted tasks", "error");
    }
  };

  const handleRestoreTask = async () => {
    if (!taskToRestore) return;

    try {
      const user_id = localStorage.getItem("User_Id");
      const sc_id = localStorage.getItem("SC_Id");

      const response = await fetch(envVariable.serverId + "/SCO/undeleteTask.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          task_id: taskToRestore.task_id,
          undeleted_by: user_id,
          sc_id: sc_id,
        }),
      });

      const result = await response.json();

      if (result.type === "success") {
        if (result.was_unassigned) {
          // Show the specific conflict reason from backend
          const message = result.conflict_reason
            ? `Task restored successfully. Mechanic was unassigned: ${result.conflict_reason}`
            : "Task restored successfully. Mechanic was unassigned due to conflicts.";
          notify(message, "warning");
        } else {
          notify("Task restored successfully", "success");
        }
        setRestoreModalOpen(false);
        setTaskToRestore(null);
        fetchDeletedTasks();
      } else {
        throw new Error(result.message || "Failed to restore task");
      }
    } catch (error) {
      console.error("Error restoring task:", error);
      notify("Error restoring task: " + error.message, "error");
    }
  };

  const navigateToChat = (task) => {
    const formattedDate = formatDateSafe(task.appointment_time || task.created_at);
    navigate(`/tasks/chat/${task.task_id}`, {
      state: {
        customerId: task.customer_id,
        customerName: task.customer_name,
        mechanicId: task.mechanic_id,
        mechanicName: task.mechanic_name,
        originDate: formattedDate,
        vin_number: task.vin_number,
        carId: task.user_car_id,
      },
    });
  };

  const totalPages = Math.max(1, Math.ceil(filteredTasks.length / itemsPerPage));

  const handleLeftPagination = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleRightPagination = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handleCustomer = (customerName) => {
    setSearchByCustomer(customerName);
    setCurrentPage(1);
  };

  const handleVin = (vin_number) => {
    setSearchByVIN(vin_number);
    setCurrentPage(1);
  };

  const handleSortFieldChange = (field) => {
    if (sortField === field) {
      // If clicking the same column, toggle the order
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      // If clicking a new column, set it and default to ascending
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const getFilteredTasks = useCallback(
    (
      customerName = searchByCustomer,
      vin_number = searchByVIN,
      sortKey = sortField,
      order = sortOrder
    ) => {
      const customerQuery = (customerName || "").toLowerCase();
      const vinQuery = (vin_number || "").toLowerCase();

      const normalizeString = (value) => (value ? value.toString().toLowerCase() : "");

      const parseDate = (value) => {
        if (!value) {
          return null;
        }
        const dateValue = new Date(value);
        return Number.isNaN(dateValue.getTime()) ? null : dateValue.getTime();
      };

      const getSortValue = (task) => {
        switch (sortKey) {
          case "deleted_at":
            return parseDate(task.deleted_at);
          case "customer_name":
            return normalizeString(task.customer_name);
          case "task_id":
          default:
            return Number(task.task_id) || 0;
        }
      };

      const sortedTasks = tasks
        .filter((task) => normalizeString(task.customer_name).includes(customerQuery))
        .filter((task) => {
          if (!vinQuery) {
            return true;
          }
          const vin = normalizeString(task.vin_number);
          return vin.includes(vinQuery);
        })
        .sort((a, b) => {
          const aValue = getSortValue(a);
          const bValue = getSortValue(b);

          if (aValue === bValue) {
            return 0;
          }

          if (aValue === null || aValue === undefined || aValue === "") {
            return 1;
          }

          if (bValue === null || bValue === undefined || bValue === "") {
            return -1;
          }

          let comparison = 0;

          if (typeof aValue === "string" && typeof bValue === "string") {
            comparison = aValue.localeCompare(bValue);
          } else {
            comparison = aValue > bValue ? 1 : -1;
          }

          return order === "asc" ? comparison : -comparison;
        });

      setFilteredTasks(sortedTasks);
    },
    [tasks, searchByCustomer, searchByVIN, sortField, sortOrder]
  );

  useEffect(() => {
    fetchDeletedTasks();
  }, []);

  useEffect(() => {
    getFilteredTasks();
  }, [getFilteredTasks]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredTasks.length / itemsPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [filteredTasks.length, currentPage, itemsPerPage]);

  const renderActionButtons = (task) => {
    return (
      <>
        <button className="chat-cls" onClick={() => navigateToChat(task)}>
          View
        </button>
        <button
          className="edit-view-cls"
          onClick={() => {
            setTaskToRestore(task);
            setRestoreModalOpen(true);
          }}
        >
          Restore
        </button>
      </>
    );
  };

  return (
    <>
      <MenuBar />

      {/* Restore Confirmation Modal */}
      <DeleteModal
        isOpen={restoreModalOpen}
        onClose={() => setRestoreModalOpen(false)}
        onConfirm={handleRestoreTask}
      >
        <h2>Restore Task</h2>
        <p>Are you sure you want to restore this task?</p>
        {taskToRestore && taskToRestore.mechanic_name && (
          <p style={{ fontSize: "14px", color: "#666" }}>
            Note: If there are scheduling conflicts, the mechanic assignment may be removed.
          </p>
        )}
      </DeleteModal>

      <div className="tab-container">
        <h2 style={{ margin: "20px 0", textAlign: "center" }}>Deleted Tasks</h2>
      </div>

      <div className="tasks-search-section">
        <div className="tasks-search-inputs">
          <div className="page-input-block">
            <label className="task-label">Customer&apos;s Name</label>
            <input
              type="text"
              placeholder="Search by Customer's Name"
              className="search-input"
              value={searchByCustomer}
              onChange={(e) => {
                handleCustomer(e.target.value);
              }}
            />
          </div>
          <div className="page-input-block">
            <label className="task-label">VIN Number</label>
            <input
              type="text"
              placeholder="Search by VIN Number"
              className="search-input"
              value={searchByVIN}
              onChange={(e) => handleVin(e.target.value)}
            />
          </div>
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/tasks")}
        >
          Back to Tasks
        </Button>
      </div>

      <div className="tasks-container">
        <table className="tasks-table">
          <thead>
            <tr>
              <th onClick={() => handleSortFieldChange("task_id")} className="sortable-header">
                Task ID {sortField === "task_id" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSortFieldChange("customer_name")} className="sortable-header">
                Customer {sortField === "customer_name" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Car Model</th>
              <th>Service Info</th>
              <th>Status</th>
              <th>VIN Number</th>
              <th onClick={() => handleSortFieldChange("deleted_at")} className="sortable-header">
                Deleted At {sortField === "deleted_at" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Deleted By</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredTasks.length !== 0 ? (
              filteredTasks
                .slice((currentPage - 1) * itemsPerPage, (currentPage - 1) * itemsPerPage + itemsPerPage)
                .map((task) => (
                  <tr key={task.task_id}>
                    <td>{task.task_id}</td>
                    <td>{task.customer_name}</td>
                    <td>{task.car_model}</td>
                    <td>{task.service_info}</td>
                    <td>{task.status}</td>
                    <td>{task.vin_number}</td>
                    <td>{task.deleted_at ? formatLocalDateTimeDisplay(task.deleted_at) : "N/A"}</td>
                    <td>{task.deleted_by_first_name && task.deleted_by_last_name
                      ? `${task.deleted_by_first_name} ${task.deleted_by_last_name}`
                      : "Unknown"}</td>
                    <td>{renderActionButtons(task)}</td>
                  </tr>
                ))
            ) : (
              <></>
            )}
          </tbody>
        </table>

        {filteredTasks.length === 0 ? (
          <p style={{ textAlign: "center", fontSize: 24, marginTop: 30 }}>No deleted tasks</p>
        ) : (
          <></>
        )}

        {/* Pagination at the bottom */}
        {filteredTasks.length > 0 && (
          <div className="pagination pagination-compact" style={{ justifyContent: "center", marginTop: "20px" }}>
            <button
              className="page-item"
              disabled={currentPage === 1}
              onClick={handleLeftPagination}
            >
              {"<"}
            </button>
            <span className="pagination-status">
              Page {currentPage} / {totalPages}
            </span>
            <button
              className="page-item"
              disabled={currentPage >= totalPages || filteredTasks.length === 0}
              onClick={handleRightPagination}
            >
              {">"}
            </button>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default DeletedTasks;
